import { InputAdornment } from "@mui/material";
import MuiTextField from "@mui/material/TextField";
import { Field } from "react-final-form";
import { useEffect, useRef, useState } from "react";

/**
 * Ввод, который держит значение локально и отдаёт его наверх с задержкой.
 * Нужен фильтрам: без него каждое нажатие клавиши уходит в GraphQL-запрос.
 */
function DebouncedInput({
  value: externalValue,
  onChange,
  debounceMs,
  ...inputProps
}: any) {
  const [localValue, setLocalValue] = useState(externalValue);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Синхронизация с внешним значением — очистка фильтров, переход по истории
  useEffect(() => {
    setLocalValue(externalValue);
  }, [externalValue]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setLocalValue(nextValue);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Отдаём значение, а не событие: к моменту срабатывания таймера
    // обработчик события уже отработал
    debounceTimer.current = setTimeout(() => {
      onChange(nextValue);
    }, debounceMs);
  };

  return (
    <MuiTextField {...inputProps} onChange={handleChange} value={localValue} />
  );
}

interface TextFieldProps {
  name: string;
  type?: "text" | "password" | "email" | "url" | "number";
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  multiline?: boolean;
  rows?: number;
  fullWidth?: boolean;
  variant?: "outlined" | "filled" | "standard";
  autoFocus?: boolean;
  nullable?: boolean;
  optional?: boolean;
  /** Задержка перед отдачей значения в форму, мс. 0 или не задано — без задержки */
  debounce?: number;
  [key: string]: any;
}

export function TextField({ debounce, ...props }: TextFieldProps) {
  const debounceMs = debounce ?? 0;

  return (
    <Field
      {...props}
      parse={(value: any) => {
        if (!value && props.optional) return undefined;
        if (!value && props.nullable) return null;

        if (props.type === "number") {
          return value ? Number(value) : 0;
        }

        return value ? String(value) : "";
      }}
      render={({
        input: { name, onChange, value, ...restInput },
        meta,
        step,
        helperText,
        startAdornment,
        endAdornment,
        maxLength,
        ...rest
      }: any) => {
        let error: boolean | null = null;
        if (maxLength) {
          helperText = `${value.length} of ${maxLength}`;
          if (value.length > maxLength) {
            error = true;
          }
        }
        const textFieldProps = {
          ...rest,
          name,
          helperText:
            (meta.touched && meta.submitError) ||
            (meta.touched && meta.error) ||
            helperText,
          error:
            (meta.submitError && meta.touched) ||
            (meta.touched && meta.error) ||
            error,
          variant: "outlined" as const,
          inputProps: {
            step,
          },
          InputProps: {
            ...restInput,
            autoComplete: "off",
            startAdornment: startAdornment && (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ),
            endAdornment: endAdornment && (
              <InputAdornment position="end">{endAdornment}</InputAdornment>
            ),
          },
          onChange,
          value,
          autoFocus: props.autoFocus,
        };

        if (debounceMs > 0) {
          return <DebouncedInput {...textFieldProps} debounceMs={debounceMs} />;
        }

        return <MuiTextField {...textFieldProps} />;
      }}
    />
  );
}
